import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { useSnackbar } from "@kitbird/core";
import { TabTypes } from "../../../Transaction/CreateReceiptSubmission/enums";
import { useDebounce } from "../../../../hooks";
import useAuth from "../../../../components/Authentication/useAuth";
import { getApprovalVendors, GetApprovalVendorsPayloadI, postApprovalVendor } from "../../../../services/approvalMasterVendorService";
import { IOptionItem } from "../../../../typings/common";
import { ModalType } from "../constants";
import { getMasterVendorDetail, IGetMasterVendorDetailResponse } from "../../../../services/masterVendorService";

const columnOptions = [
  { label: "Kode vendor", value: "vendor_code", checked: true },
  { label: "Nama & alamat vendor", value: "vendor_name", checked: true },
  { label: "Tipe vendor", value: "type_vendor", checked: true },
  { label: "Detail rekening vendor", value: "bank_account_name", checked: true },
  { label: "Lampiran", value: "vendor_files", checked: true },
];

const useApprovalVendor = () => {
  const { userInfo } = useAuth();
  const snackbar = useSnackbar();

  const obsRef = useRef(null);
  const tableRef = useRef(document.createElement("table"));

  const [searchText, setSearchText] = useState("");
  const [params, setParams] = useState<GetApprovalVendorsPayloadI>({
    status_approval: TabTypes.OPEN,
    employee_email: userInfo.email,
    page_number: 1,
    page_size: 10,
    sort_by: "",
    sort_type: "DSC",
  });
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });

  const [columnOpts, setColumnOpts] = useState<IOptionItem[]>(JSON.parse(localStorage.getItem("COLUMN_APPROVAL_VENDOR") || JSON.stringify(columnOptions)));
  const [hiddenColumn, setHiddenColumn] = useState<string[]>([]);
  const [detailRow, setDetailRow] = useState<any>();
  const [detail, setDetail] = useState<IGetMasterVendorDetailResponse>();
  const [isOpen, setOpen] = useState<ModalType>();
  const [rejectReason, setRejectReason] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [isLoadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    let isAllHidden = true;
    const hiddenCol = columnOpts.map((opt) => {
      let val = "";
      if (!opt.checked) val = opt.value.toString();
      else isAllHidden = false;
      return val;
    });
    if (isAllHidden) {
      setColumnOpts(columnOptions);
      localStorage.setItem("COLUMN_APPROVAL_VENDOR", JSON.stringify(columnOptions));
      setHiddenColumn([]);
    } else {
      localStorage.setItem("COLUMN_APPROVAL_VENDOR", JSON.stringify(columnOpts));
      setHiddenColumn(hiddenCol);
    }
  }, [columnOpts]);

  useEffect(() => {
    setParams((curr) => ({ ...curr, search: searchText }));
  }, [useDebounce(searchText, 300)]);

  useEffect(() => {
    if (!isOpen) {
      setDetail(undefined);
      setDetailRow(undefined);
    }
  }, [isOpen]);

  const fetchList = async (pageParam: number) => {
    const sortBy = params.sort_type === "DEFAULT" ? "" : params.sort_by;
    const sortType = params.sort_type !== "ASC" ? "DESC" : "ASC";

    return getApprovalVendors({
      ...params,
      page_number: pageParam,
      sort_by: sortBy,
      sort_type: sortType,
    }).then((res) => {
      if (res.metadata) setMeta(res.metadata);
      return res.list_approval_mst_vendor;
    }).catch(() => []);
  };

  const {
    data: approvalVendorPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ["approvalVendorList", params],
    async ({ queryKey, pageParam = 1 }) => (
      fetchList(pageParam)
    ),
    {
      getNextPageParam: () => (
        meta.page < meta.total_pages
          ? meta.page + 1
          : undefined
      ),
      keepPreviousData: true,
    },
  );

  const approvalVendorList = useMemo(
    () => approvalVendorPages.pages.flatMap((page) => page),
    [approvalVendorPages, params],
  );

  const options = {
    root: obsRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(obsRef.current || document.createElement("tfoot"));
    else observer.observe(obsRef.current || document.createElement("tfoot"));
  }, [meta]);

  const fetchDetail = async (id: string) => {
    setLoadingDetail(true);
    getMasterVendorDetail(id).then((res) => {
      setDetail(res);
    }).finally(() => setLoadingDetail(false));
  };

  useEffect(() => {
    if (detailRow) fetchDetail(detailRow.vendor_code);
  }, [detailRow]);

  const isApprove = isOpen === ModalType.APPROVE;

  const handleApprove = async () => {
    setLoading(true);
    if (detail) {
      postApprovalVendor(isApprove ? "approve" : "reject", {
        vendor_code: detail.vendor_code,
        employee_email: userInfo.email,
        reason: (isApprove ? "APPROVED" : rejectReason),
        update_by: userInfo.preferred_username,
      }).then(() => {
        refetchTable();
        snackbar.show(`Master vendor telah ${isApprove ? "disetujui" : "ditolak"}`, "success");
        setOpen(undefined);
        setDetailRow(undefined);
      }).catch(() => {
        snackbar.show(`Master vendor gagal ${isApprove ? "disetujui" : "ditolak"}`, "error");
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
      snackbar.show("Master vendor gagal menampilkan detail", "error");
    }
  };
  return {
    obsRef,
    tableRef,
    searchText,
    setSearchText,
    meta,
    setMeta,
    params,
    setParams,
    columnOpts,
    setColumnOpts,
    hiddenColumn,
    detailRow,
    setDetailRow,
    detail,
    isOpen,
    setOpen,
    rejectReason,
    setRejectReason,
    isLoading,
    setLoading,
    isLoadingDetail,

    isFetching,
    isFetchingNextPage,

    approvalVendorList,

    handleApprove,
  };
};

export default useApprovalVendor;
